import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Standardized delete confirmation modal for reuse across pages.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {() => void | Promise<void>} props.onConfirm — closes only after resolve
 * @param {string} [props.title="Confirmar exclusão"]
 * @param {React.ReactNode} [props.description]
 * @param {React.ReactNode} [props.itemName] — highlighted target (code, name, etc.)
 * @param {string} [props.confirmLabel="Excluir"]
 * @param {string} [props.cancelLabel="Cancelar"]
 * @param {string} [props.confirmLoadingLabel="Excluindo..."]
 * @param {string} [props.testId="confirm-delete-dialog"]
 */
export default function ConfirmDeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  title = "Confirmar exclusão",
  description = "Esta ação não poderá ser desfeita facilmente.",
  itemName,
  confirmLabel = "Excluir",
  cancelLabel = "Cancelar",
  confirmLoadingLabel = "Excluindo...",
  testId = "confirm-delete-dialog",
}) {
  const [busy, setBusy] = useState(false);

  const handleOpenChange = (next) => {
    if (busy && !next) return;
    onOpenChange?.(next);
  };

  const handleCancel = () => {
    if (busy) return;
    onOpenChange?.(false);
  };

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange?.(false);
    } catch {
      // Keep open so the user can retry or cancel after an error toast.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="rounded-3xl max-w-md gap-5"
        data-testid={testId}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader className="sm:text-left space-y-3">
          <div className="flex items-start gap-3">
            <div
              className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive"
              aria-hidden
            >
              <Trash2 size={18} />
            </div>
            <div className="min-w-0 space-y-1.5 text-left">
              <DialogTitle className="font-display font-bold text-lg leading-snug">
                {title}
              </DialogTitle>
              {itemName != null && itemName !== "" && (
                <p
                  className="text-sm font-medium text-[var(--text-1)] break-words"
                  data-testid={`${testId}-item`}
                >
                  {itemName}
                </p>
              )}
              <DialogDescription className="text-sm leading-relaxed">
                {description || "Esta ação não poderá ser desfeita facilmente."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            className="rounded-xl"
            onClick={handleCancel}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            className="rounded-xl"
            onClick={handleConfirm}
            data-testid={`${testId}-confirm`}
          >
            {busy ? confirmLoadingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
